/**
 * Canonical auth code glossary + non-refresh failure classifier
 * (permanent-session-continuity design §6.1, §6.3).
 *
 * These arrays MUST stay byte-identical, in this order, to
 * MyAMSMobile/src/redux/api/authCodes.ts and MyAMSBackend/utils/authErrorCodes.js.
 */

export const TERMINAL_AUTH_CODES = Object.freeze([
  'SESSION_REVOKED',
  'TOKEN_FAMILY_REVOKED', // reserved (legacy glossary; never emitted)
  'SESSION_ABSOLUTE_MAXIMUM_REACHED', // reserved (legacy glossary; never emitted)
  'DEVICE_REVOKED',
  'DEVICE_APPROVAL_PENDING',
  'REFRESH_EXPIRED',
  'REFRESH_INVALID',
  'REFRESH_TOKEN_MISSING',
  'USER_INACTIVE',
]);

export const REFRESHABLE_AUTH_CODES = Object.freeze([
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
  'INVALID_TOKEN', // existing validateActiveAuthSession code (token lacks user id)
  'SESSION_INVALID', // existing verifyToken fallback
  'SESSION_EXPIRED', // row missing → refresh will decide (refresh also validates the row)
]);

export const TRANSIENT_AUTH_CODES = Object.freeze([
  'AUTH_CHECK_FAILED',
  'SERVICE_STARTING',
]);

/**
 * Classify a non-refresh auth failure (design §6.3).
 * @returns {'transient'|'terminal'|'refresh'|'ignore'|'legacy'}
 *   'legacy' → no code: caller runs its pre-existing (old-backend) logic unchanged.
 */
export function classifyAuthFailure(input) {
  try {
    const status = input?.status;
    if (status === null || status === undefined) return 'transient';
    if (status === 502 || status === 503 || status === 504) return 'transient';
    const c = typeof input?.code === 'string' ? input.code : '';
    if (c && TRANSIENT_AUTH_CODES.includes(c)) return 'transient';
    if (c && TERMINAL_AUTH_CODES.includes(c)) return 'terminal';
    if (c && REFRESHABLE_AUTH_CODES.includes(c)) return status === 401 ? 'refresh' : 'ignore';
    if (c) return 'ignore';
    return 'legacy';
  } catch (e) {
    console.warn('[authCodes] classifyAuthFailure failed:', e?.message || e);
    return 'legacy';
  }
}
